/**
 * 工作流画布编辑器组件
 *
 * 核心画布组件，集成 React Flow 和所有辅助功能
 * 支持列表/编辑器视图切换
 * 使用 REST API + SSE 替代 WebSocket
 */

import { useCallback, useRef, useState, useEffect, type DragEvent, type ReactNode } from 'react';
import { clsx } from 'clsx';
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  SelectionMode,
  Panel,
  useReactFlow,
  type Node,
  type Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { useWorkflowStore, useExecutionStore } from '../../store/workflowStore';
import { useValidateConnection } from '../../hooks/useValidateConnection';
import { useShortcutKeys } from '../../hooks/useShortcutKeys';
import { nodeTypes, nodeTypeColors } from './nodes';
import { edgeTypes, defaultEdgeOptions } from './edges';
import { NodeConfigPanel } from './config/NodeConfigPanel';
import { NodeToolbar } from './NodeToolbar';
import { ExecutionProgressBar } from './ExecutionProgressBar';
import { DebugToolbar } from './DebugToolbar';
import { NodeLogPanel } from './NodeLogPanel';
import { ExecutionContextPanel } from './ExecutionContextPanel';
import { WorkflowListPanel } from './WorkflowListPanel';
import { AIGeneratedNotice } from '../ui/AIGeneratedNotice';
import { workflowApi } from '../../services/workflowApi';
import type { WorkflowNodeType } from '../../types/workflow';
import { ExecutionState as ExecState } from '../../types/workflow';

// ==================== 主画布组件 ====================

interface WorkflowCanvasProps {
  /** 工作区 ID */
  workspaceId: number;
  /** 初始工作流 ID（用于加载已有工作流） */
  initialWorkflowId?: string;
  /** 是否显示工具栏 */
  showToolbar?: boolean;
  /** 是否显示配置面板 */
  showConfigPanel?: boolean;
  /** 是否显示小地图 */
  showMiniMap?: boolean;
  /** 是否显示控制按钮 */
  showControls?: boolean;
  /** 是否显示背景网格 */
  showBackground?: boolean;
  /** 是否启用网格对齐 */
  snapToGrid?: boolean;
  /** 网格大小 */
  snapGrid?: [number, number];
  /** 最小缩放 */
  minZoom?: number;
  /** 最大缩放 */
  maxZoom?: number;
  /** 子组件（用于扩展） */
  children?: ReactNode;
  /** 自定义顶部面板 */
  topPanel?: ReactNode;
  /** 自定义底部面板 */
  bottomPanel?: ReactNode;
  /** 工作流保存回调 */
  onWorkflowSave?: (workflowId: number) => void;
}

function WorkflowCanvas({
  workspaceId,
  initialWorkflowId,
  showToolbar = true,
  showConfigPanel = true,
  showMiniMap = true,
  showControls = true,
  showBackground = true,
  snapToGrid = true,
  snapGrid = [20, 20],
  minZoom = 0.1,
  maxZoom = 2,
  children,
  topPanel,
  bottomPanel,
  onWorkflowSave,
}: WorkflowCanvasProps) {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { screenToFlowPosition } = useReactFlow();

  // 从 store 获取状态和方法
  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    addNode,
    selectedNodeId,
    setSelectedNodeId,
    isDirty,
    markClean,
    workflowId,
    setWorkflow,
  } = useWorkflowStore();

  // 从 executionStore 获取执行相关方法和状态
  const {
    resetExecution,
    setExecutionState,
    setError,
  } = useExecutionStore();

  // SSE AbortController for execution
  const executionAbortRef = useRef<AbortController | null>(null);
  const [executionId, setExecutionId] = useState<string | null>(null);

  // 加载已有工作流
  useEffect(() => {
    const loadWorkflow = async () => {
      const targetWorkflowId = workflowId || initialWorkflowId;
      if (!targetWorkflowId) return;

      if (nodes.length > 0) return;

      try {
        const data = await workflowApi.getWorkflow(parseInt(targetWorkflowId, 10));
        if (data) {
          setWorkflow(String(data.id), data.name, data.nodes as any[], data.edges as any[]);
          console.log('[WorkflowEditor] 已加载工作流:', data.id);
        }
      } catch (error) {
        console.error('[WorkflowEditor] 加载工作流失败:', error);
      }
    };

    loadWorkflow();
  }, [workflowId, initialWorkflowId, setWorkflow, nodes.length]);

  // 连接验证
  const { isValidConnection } = useValidateConnection(nodes, edges);

  // ==================== 执行控制函数 ====================

  /**
   * 保存工作流到后端
   */
  const handleSaveWorkflow = useCallback(async () => {
    try {
      const workflowData = {
        name: '工作流',
        nodes: nodes.map(n => ({
          id: n.id,
          type: n.type,
          position: n.position,
          data: n.data,
        })),
        edges: edges.map(e => ({
          id: e.id,
          source: e.source,
          target: e.target,
          sourceHandle: e.sourceHandle,
          targetHandle: e.targetHandle,
          type: e.type,
          data: e.data,
        })),
      };

      if (workflowId) {
        await workflowApi.updateWorkflow(parseInt(workflowId, 10), workflowData);
        markClean();
        onWorkflowSave?.(parseInt(workflowId, 10));
        console.log('[WorkflowEditor] 工作流已保存:', workflowId);
      } else {
        const created = await workflowApi.createWorkflow({
          workspace_id: workspaceId,
          ...workflowData,
        });
        if (created?.id) {
          setWorkflow(String(created.id), workflowData.name, nodes, edges);
          markClean();
          onWorkflowSave?.(created.id);
          console.log('[WorkflowEditor] 工作流已创建:', created.id);
        }
      }
    } catch (error) {
      console.error('[WorkflowEditor] 保存工作流失败:', error);
      const errorMsg = error instanceof Error ? error.message : '保存工作流失败';
      setError(errorMsg);
    }
  }, [nodes, edges, workflowId, workspaceId, markClean, setWorkflow, onWorkflowSave, setError]);

  /**
   * 开始执行工作流
   */
  const handleStartExecution = useCallback(async () => {
    const startNode = nodes.find(n => n.type === 'start');
    if (!startNode) {
      setError('工作流必须包含一个开始节点');
      return;
    }

    const taskPrompt = (startNode.data as Record<string, unknown>)?.task_prompt as string;
    if (!taskPrompt?.trim()) {
      setError('开始节点的任务 prompt 不能为空');
      return;
    }

    try {
      if (isDirty || !workflowId) {
        await handleSaveWorkflow();
      }

      const currentWorkflowId = useWorkflowStore.getState().workflowId;
      if (!currentWorkflowId) {
        setError('请先保存工作流');
        return;
      }

      resetExecution();

      // Abort any existing execution
      if (executionAbortRef.current) {
        executionAbortRef.current.abort();
      }
      executionAbortRef.current = new AbortController();

      const abortSignal = executionAbortRef.current.signal;

      // Consume SSE events from execution
      (async () => {
        try {
          for await (const event of workflowApi.executeWorkflow(
            parseInt(currentWorkflowId, 10),
            workspaceId,
            {
              task_prompt: taskPrompt,
              debug_mode: false,
            },
            abortSignal
          )) {
            const eventType = event.type;
            const eventData = event.data;

            if (eventType === 'started') {
              const execId = eventData?.execution_id as string;
              setExecutionId(execId);
              console.log('[WorkflowEditor] 工作流执行已启动:', execId);
            } else if (eventType === 'state_change') {
              setExecutionState(ExecState.RUNNING);
            } else if (eventType === 'node_started') {
              // Node started
            } else if (eventType === 'node_finished') {
              // Node completed
            } else if (eventType === 'error') {
              setExecutionState(ExecState.FAILED);
              const errorMsg = eventData?.error as string || '执行失败';
              setError(errorMsg);
            } else if (eventType === 'done') {
              setExecutionState(ExecState.COMPLETED);
              console.log('[WorkflowEditor] 工作流执行完成');
            }
          }
        } catch (err) {
          if ((err as Error).name !== 'AbortError') {
            console.error('[WorkflowEditor] 执行SSE错误:', err);
            setExecutionState(ExecState.FAILED);
            setError(err instanceof Error ? err.message : '执行失败');
          }
        }
      })();
    } catch (error) {
      console.error('[WorkflowEditor] 启动执行失败:', error);
      setExecutionState(ExecState.FAILED);
      setError(error instanceof Error ? error.message : '启动执行失败');
    }
  }, [
    nodes,
    isDirty,
    workflowId,
    workspaceId,
    resetExecution,
    handleSaveWorkflow,
    setExecutionState,
    setError,
  ]);

  /**
   * 暂停执行
   */
  const handlePauseExecution = useCallback(async () => {
    if (!executionId) return;

    try {
      await workflowApi.pauseExecution(executionId);
      console.log('[WorkflowEditor] 已发送暂停请求');
    } catch (error) {
      console.error('[WorkflowEditor] 暂停执行失败:', error);
      setError(error instanceof Error ? error.message : '暂停执行失败');
    }
  }, [executionId, setError]);

  /**
   * 恢复执行
   */
  const handleResumeExecution = useCallback(async () => {
    if (!executionId) return;

    try {
      await workflowApi.resumeExecution(executionId);
      console.log('[WorkflowEditor] 已发送恢复请求');
    } catch (error) {
      console.error('[WorkflowEditor] 恢复执行失败:', error);
      setError(error instanceof Error ? error.message : '恢复执行失败');
    }
  }, [executionId, setError]);

  /**
   * 单步执行
   */
  const handleStepExecution = useCallback(async () => {
    if (!executionId) {
      useExecutionStore.getState().setStepMode(true);
      await handleStartExecution();
      return;
    }

    try {
      await workflowApi.stepExecution(executionId);
      console.log('[WorkflowEditor] 已发送单步执行请求');
    } catch (error) {
      console.error('[WorkflowEditor] 单步执行失败:', error);
      setError(error instanceof Error ? error.message : '单步执行失败');
    }
  }, [executionId, handleStartExecution, setError]);

  /**
   * 中断执行
   */
  const handleInterruptExecution = useCallback(async () => {
    if (!executionId) return;

    try {
      await workflowApi.stopExecution(executionId);
      if (executionAbortRef.current) {
        executionAbortRef.current.abort();
        executionAbortRef.current = null;
      }
      console.log('[WorkflowEditor] 已发送中断请求');
    } catch (error) {
      console.error('[WorkflowEditor] 中断执行失败:', error);
      setError(error instanceof Error ? error.message : '中断执行失败');
    }
  }, [executionId, setError]);

  /**
   * 重新执行
   */
  const handleRestartExecution = useCallback(async () => {
    if (executionId) {
      await handleInterruptExecution();
    }
    resetExecution();
    await handleStartExecution();
  }, [executionId, handleInterruptExecution, resetExecution, handleStartExecution]);

  // ==================== 快捷键处理 ====================

  useShortcutKeys({
    enableNodeShortcuts: true,
    enableCanvasShortcuts: true,
    enableDebugShortcuts: true,
    enableSelectionShortcuts: true,
    onSave: useCallback(() => {
      handleSaveWorkflow();
    }, [handleSaveWorkflow]),
    onAutoLayout: useCallback(() => {
      console.log('自动布局');
    }, []),
  });

  // 拖拽状态
  const [isDragOver, setIsDragOver] = useState(false);

  // 处理节点点击
  const onNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      setSelectedNodeId(node.id);
    },
    [setSelectedNodeId]
  );

  // 处理画布点击（取消选择）
  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
  }, [setSelectedNodeId]);

  // 处理节点删除
  const onNodesDelete = useCallback(
    (deletedNodes: Node[]) => {
      deletedNodes.forEach((node) => {
        if (selectedNodeId === node.id) {
          setSelectedNodeId(null);
        }
      });
    },
    [selectedNodeId, setSelectedNodeId]
  );

  // 处理边删除
  const onEdgesDelete = useCallback(
    (_deletedEdges: Edge[]) => {
      // 边删除由 React Flow 自动处理
    },
    []
  );

  // 处理拖拽进入
  const onDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  }, []);

  // 处理拖拽离开
  const onDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  }, []);

  // 处理拖放
  const onDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragOver(false);

      const nodeType = event.dataTransfer.getData('application/reactflow') as WorkflowNodeType;
      if (!nodeType) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const snappedPosition = {
        x: Math.round(position.x / snapGrid[0]) * snapGrid[0],
        y: Math.round(position.y / snapGrid[1]) * snapGrid[1],
      };

      addNode(nodeType, snappedPosition);
    },
    [screenToFlowPosition, snapGrid, addNode]
  );

  // 处理节点拖拽结束
  const onNodeDragStop = useCallback(
    (_event: React.MouseEvent, _node: Node, nodes: Node[]) => {
      if (nodes.length !== 1) return;

      const draggedNode = nodes[0];
      if (draggedNode.type === 'loop') return;

      console.log('[onNodeDragStop] 节点拖拽结束:', draggedNode.id, '位置:', draggedNode.position, 'parentId:', draggedNode.parentId);

      const store = useWorkflowStore.getState();
      store.updateNodeParent(draggedNode.id, draggedNode.position);
    },
    []
  );

  // 获取选中节点
  const selectedNode = nodes.find((n) => n.id === selectedNodeId);

  return (
    <div
      ref={reactFlowWrapper}
      className={`w-full h-full relative ${isDragOver ? 'bg-blue-50/50' : ''}`}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onNodesDelete={onNodesDelete}
        onEdgesDelete={onEdgesDelete}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onNodeDragStop={onNodeDragStop}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        isValidConnection={isValidConnection}
        snapToGrid={snapToGrid}
        snapGrid={snapGrid}
        minZoom={minZoom}
        maxZoom={maxZoom}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        selectionMode={SelectionMode.Partial}
        panOnDrag={[1, 2]}
        zoomOnScroll={true}
        zoomOnPinch={true}
        preventScrolling={true}
        deleteKeyCode={['Backspace', 'Delete']}
        multiSelectionKeyCode={['Shift']}
        proOptions={{ hideAttribution: true }}
        className="bg-gray-50"
      >
        {/* 顶部面板 */}
        {topPanel && <Panel position="top-center">{topPanel}</Panel>}

        {/* 顶部工具栏：保存按钮 + 调试工具栏 */}
        <Panel position="top-center">
          <div className="flex items-center gap-3 bg-white rounded-lg border border-gray-200 shadow-sm px-3 py-2">
            {/* 保存按钮 */}
            <button
              onClick={handleSaveWorkflow}
              disabled={!isDirty}
              className={clsx(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium',
                'border transition-all duration-150',
                'focus:outline-none focus:ring-2 focus:ring-paper-500 focus:ring-offset-1',
                isDirty
                  ? 'bg-ink-800 text-white border-paper-500 hover:bg-ink-700'
                  : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
              )}
              title={isDirty ? '保存工作流 (Ctrl+S)' : '工作流已保存'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
              保存
              {isDirty && (
                <span className="w-2 h-2 bg-yellow-400 rounded-full" title="有未保存的更改" />
              )}
            </button>

            {/* 分隔线 */}
            <div className="w-px h-6 bg-gray-200" />

            {/* 调试工具栏 */}
            <DebugToolbar
              compact
              showShortcutHints={false}
              onStart={handleStartExecution}
              onPause={handlePauseExecution}
              onResume={handleResumeExecution}
              onStep={handleStepExecution}
              onInterrupt={handleInterruptExecution}
              onRestart={handleRestartExecution}
            />
          </div>
        </Panel>

        {/* 执行进度条 */}
        <ExecutionProgressBar />

        {/* 左侧工具栏 */}
        {showToolbar && (
          <Panel position="top-left">
            <NodeToolbar />
          </Panel>
        )}

        {/* 小地图 */}
        {showMiniMap && (
          <MiniMap
            nodeColor={(node: Node) => {
              const colors = nodeTypeColors[node.type || ''] || nodeTypeColors.agent;
              return colors.bg.replace('bg-', '').replace('-50', '-200');
            }}
            maskColor="rgba(0, 0, 0, 0.1)"
            className="bg-white border border-gray-200 rounded-lg shadow-sm"
          />
        )}

        {/* 控制按钮 */}
        {showControls && (
          <Controls
            showZoom={true}
            showFitView={true}
            showInteractive={false}
            className="bg-white border border-gray-200 rounded-lg shadow-sm"
          />
        )}

        {/* 背景网格 */}
        {showBackground && (
          <Background
            variant={BackgroundVariant.Dots}
            gap={20}
            size={1}
            color="#d1d5db"
          />
        )}

        {/* 底部面板 */}
        {bottomPanel && <Panel position="bottom-center">{bottomPanel}</Panel>}
      </ReactFlow>

      {/* 右侧配置面板 */}
      {showConfigPanel && selectedNode && (
        <div className="absolute right-0 top-0 h-full w-80 bg-white border-l border-gray-200 shadow-lg z-10">
          <NodeConfigPanel
            nodeId={selectedNode.id}
            onClose={() => setSelectedNodeId(null)}
          />
        </div>
      )}

      {/* 子组件 */}
      {children}

      {/* 底部调试面板 */}
      <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-10">
        <div className="flex h-auto max-h-[300px]">
          <div className="w-1/3 border-r border-gray-200 overflow-hidden">
            <ExecutionContextPanel
              compact
              showTitle
              collapsible
              defaultExpanded
            />
          </div>

          <div className="flex-1 overflow-hidden">
            <NodeLogPanel
              compact
              showTitle
              collapsible
              defaultExpanded
              maxHeight="280px"
              selectedNodeId={selectedNodeId}
            />
          </div>
        </div>
        <div className="px-3 py-1 bg-white border-t border-gray-100 flex items-center justify-center">
          <AIGeneratedNotice variant="block" className="py-0" />
        </div>
      </div>
    </div>
  );
}

// ==================== 编辑器包装组件 ====================

export interface WorkflowEditorProps extends WorkflowCanvasProps {
  // 所有属性已从 WorkflowCanvasProps 继承
}

export function WorkflowEditor({
  workspaceId,
  initialWorkflowId,
  onWorkflowSave,
  ...props
}: WorkflowEditorProps) {
  const { viewMode, showList, showEditor, clearWorkflow } = useWorkflowStore();

  const handleSelectWorkflow = useCallback(
    (workflowId: string) => {
      showEditor(workflowId);
    },
    [showEditor]
  );

  const handleCreateWorkflow = useCallback(() => {
    clearWorkflow();
    showEditor();
  }, [clearWorkflow, showEditor]);

  const handleBackToList = useCallback(() => {
    showList();
  }, [showList]);

  const handleWorkflowSaveSuccess = useCallback(
    (workflowId: number) => {
      onWorkflowSave?.(workflowId);
    },
    [onWorkflowSave]
  );

  useEffect(() => {
    if (initialWorkflowId) {
      showEditor(initialWorkflowId);
    }
  }, [initialWorkflowId, showEditor]);

  return (
    <ReactFlowProvider>
      <div className="w-full h-full flex flex-col">
        {/* 列表视图 */}
        {viewMode === 'list' && (
          <WorkflowListPanel
            workspaceId={workspaceId}
            onSelectWorkflow={handleSelectWorkflow}
            onCreateWorkflow={handleCreateWorkflow}
          />
        )}

        {/* 编辑器视图 */}
        {viewMode === 'editor' && (
          <div className="relative flex-1">
            {/* 返回按钮 */}
            <button
              onClick={handleBackToList}
              className="absolute top-2 left-2 z-20 flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
              title="返回工作流列表"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              返回
            </button>
            
            <WorkflowCanvas
              workspaceId={workspaceId}
              initialWorkflowId={initialWorkflowId}
              onWorkflowSave={handleWorkflowSaveSuccess}
              {...props}
            />
          </div>
        )}
      </div>
    </ReactFlowProvider>
  );
}

export default WorkflowEditor;
